/**
 * What a CONFIRMED SKU swap owes the product once the new style's catalog has
 * loaded (Jake, 2026-10-07 for the rules; 2026-10-15 for where they live).
 *
 * WHERE THIS RUNS. It used to run from the Supplier & SKU panel's own catalog
 * watcher, and the pending state was a component ref. The panel is only
 * mounted while its tab is active, so leaving the tab before the catalog
 * arrived lost the clean-up: stale colors stayed selected, the old style's
 * images stayed, and the name and description were never overwritten (card
 * #349, the likely cause of #338). The panel now ARMS the clean-up in the
 * editor store (`pendingSwapCleanup`) and the always-mounted editor shell
 * RUNS it, from the shell's own catalog fetch, on whatever tab is showing.
 *
 * A FAILED LOOKUP CANCELS THE SWAP. When the catalog cannot be loaded the
 * product is put back on its previous SKU / style / supplier and the user is
 * told; nothing else had changed yet (see failSwapCleanup).
 *
 * THE RULES (unchanged):
 *   COLORS  every selected color the new style also offers stays (matched by
 *           name, the way the editor matches names everywhere); the rest are
 *           deselected; only when none survives does the new style's default
 *           color apply, as a DELIBERATE pick (#338, Jake 2026-10-22): the
 *           swap was confirmed, so it renders once via the save debounce
 *           when the product has a placement.
 *   SIZES   the same rule as colors (card #330): every selected size the
 *           new style offers stays (XXL and 2XL are one size); the rest are
 *           deselected through the store's deselectSize, so their per-size
 *           pricing goes too; only when none survives does the new-product
 *           default set apply, limited to the sizes the new style offers. An
 *           unknown size run changes nothing.
 *   IMAGES  nothing image-wise carries over, a kept color's included.
 *   COPY    the product name and descriptions are overwritten with the new
 *           style's catalog values (a description is left alone when the new
 *           style has none: losing text is worse than keeping stale text).
 */
import { watch } from 'vue'
import { pickDefaultColor } from '@/utils/defaultColorPick'
import { withoutColors } from '@/utils/storefrontImages'
import { normalizeColorNameForMatch } from '@/utils/colorNameMatch'
import { cleanCatalogName, decodeHtmlEntities } from '@/utils/catalogCopy'
import { useStorefrontColorRendersStore } from '@/stores/storefrontColorRenders'
import {
  findSameSize,
  sizesOffered,
  supplierSizeRun,
} from '@/pages/distributor/store-editor/StoreProducts/supplierSizes'
import { NEW_PRODUCT_DEFAULT_SIZES } from './sizeConstants'

export const SWAP_LOOKUP_FAILED_MESSAGE
  = 'The new item\'s catalog could not be loaded, so the change was cancelled '
  + 'and the product is back to what it was. Pick the item again to retry.'

// A swap the SERVER already committed (the Change SKU dialog's one call,
// card #545: the SKU together with placement moves/removals) is never rolled
// back client-side: writing the old SKU back would leave it with the new
// style's placement locations. Only the catalog-driven clean-up is missing.
export const SWAP_COMMITTED_LOOKUP_FAILED_MESSAGE
  = 'The SKU change and your placement choices are saved, but the new item\'s '
  + 'catalog could not be loaded, so its name, description, colors and sizes '
  + 'were not updated. Pick the item again to finish.'

// The fields a swap stages to point the product at the new item. A failed
// lookup puts these back (see failSwapCleanup).
const SWAP_IDENTITY_FIELDS = [
  'product_item_number',
  'supplier_style_id',
  'supplier_id',
  'supplier_name',
]

// What a supplier SWITCH clears along with the SKU (see the panel's
// clearSkuForSupplierSwitch): captured BEFORE the clear so a failed lookup
// can put the whole product back, not just the identity (card #542).
const SWITCH_CLEARED_FIELDS = [
  'selected_color_skus',
  'selected_colors',
  'storefront_images',
  'default_image',
  'images',
]

/**
 * Everything a supplier switch is about to clear, read from the EFFECTIVE
 * values: the swap identity plus the colors and listing images chosen against
 * the old garment. Held in the store (`supplierSwitchSnapshot`) until the swap
 * that follows either completes or fails.
 */
export function snapshotSupplierSwitch(store) {
  const snapshot = snapshotSwapIdentity(store)
  for (const field of SWITCH_CLEARED_FIELDS) {
    snapshot[field] = store.getEffectiveAttribute(field) ?? null
  }
  return snapshot
}

/**
 * What the product pointed at before a swap, read from the EFFECTIVE values
 * (an unsaved edit counts). Armed with the swap so a failed lookup can undo it.
 */
export function snapshotSwapIdentity(store) {
  const previous = {}
  for (const field of SWAP_IDENTITY_FIELDS) {
    previous[field] = store.getEffectiveAttribute(field) ?? null
  }
  return previous
}

/**
 * Select the catalog's default color as the product's ONLY color.
 *
 * The one mechanism behind both the new-product default and the
 * confirmed-swap reset. Returns the picked color_sku, or null when the
 * catalog holds no usable color, in which case NOTHING is written (an empty
 * array would dirty the record for no benefit and, on a still-warming
 * catalog, persist an empty choice).
 *
 * `isRenderable` (optional) is the editor's renderability predicate: a color
 * it rejects is never picked, and when it rejects EVERY color nothing is
 * written (an unrenderable color is never selected on the user's behalf).
 */
export function selectDefaultColor(
  store,
  catalogColors,
  { system = true, isRenderable } = {},
) {
  const colors = Array.isArray(catalogColors) ? catalogColors : []
  const pick = pickDefaultColor(colors, { isRenderable })
  if (!pick) return null

  // A NEW product's default is a SYSTEM selection, not the distributor's:
  // tell the per-color render manager so it does not spend a storefront
  // render on it (#4071 gate, Jake 2026-10-06). A confirmed swap's default is
  // deliberate (`system: false`, Jake 2026-10-22, card #338) and renders once
  // through the save debounce. Resolved lazily: some specs mount with no
  // active Pinia.
  if (system) {
    let colorRenders = null
    try {
      colorRenders = useStorefrontColorRendersStore()
    } catch (err) {
      console.warn('[useSkuSwapCleanup] storefront render store unavailable:', err?.message ?? err)
    }
    colorRenders?.noteAutoSelectedColors?.([pick])
  }
  store.updateField('selected_color_skus', [pick])
  return pick
}

/**
 * Remove every image that came from the previous style:
 *   - `storefront_images`: the chosen listing images for every color, selected
 *     or not (through withoutColors so the #4154 global positions stay valid).
 *   - `default_image`: the listing's "Set as default" color.
 *   - `images`: the legacy per-color photo map the shopper card falls back to.
 * Each field is written only when it holds something.
 */
export function clearListingImages(store) {
  const listing = store.getEffectiveAttribute('storefront_images') ?? {}
  const emptied = withoutColors(listing, Object.keys(listing))
  if (emptied !== listing) store.updateField('storefront_images', emptied)

  if (store.getEffectiveAttribute('default_image')) {
    store.updateField('default_image', null)
  }
  const legacy = store.getEffectiveAttribute('images')
  if (legacy && typeof legacy === 'object' && Object.keys(legacy).length > 0) {
    store.updateField('images', {})
  }
}

function reconcileColors(store, oldNames, catalog, isRenderable, styleUnrenderable = false) {
  const rows = Array.isArray(catalog) ? catalog : []
  const byName = new Map()
  for (const row of rows) {
    const key = normalizeColorNameForMatch(row?.name ?? row?.color_sku)
    if (key && row?.color_sku && !byName.has(key)) byName.set(key, row.color_sku)
  }
  let kept = []
  for (const name of oldNames) {
    const sku = byName.get(normalizeColorNameForMatch(name))
    if (sku && !kept.includes(sku)) kept.push(sku)
  }

  // A style the editor can render NO color of (Jake, 2026-10-29): nothing
  // carries over, because nothing can render. Select nothing; the Colors panel
  // then asks the user to pick.
  if (styleUnrenderable && rows.length > 0) kept = []

  if (kept.length > 0) {
    store.updateField('selected_color_skus', kept)
  } else if (!selectDefaultColor(store, rows, { system: false, isRenderable })) {
    if (rows.some((r) => r?.color_sku) && (styleUnrenderable || isRenderable)) {
      // The new catalog HAS colors but none is renderable: clear the old
      // style's selection rather than strand it on colors that do not exist
      // here. (Empty selection is a handled state: Stage empty state, count 0.)
      store.updateField('selected_color_skus', [])
      store.updateField('selected_colors', [])
    }
    // Otherwise no usable color in the new catalog (not loaded): leave the old
    // list for the server-side reconcile to trim.
    return
  }

  // A kept color's resolved identity (artifi_variant_sku, mainframe_code) was
  // resolved against the OLD style: write it unresolved, as ColorsPanel does
  // when resolution is unavailable.
  store.updateField(
    'selected_colors',
    kept.map((sku) => ({ color_name: sku, mainframe_code: null, artifi_variant_sku: null })),
  )
}

function reconcileSizes(store, newMeta) {
  const run = supplierSizeRun(newMeta)
  if (!run) return
  const selected = store.getEffectiveAttribute('selected_sizes')
  if (!Array.isArray(selected) || selected.length === 0) return

  const dropped = selected.filter((size) => findSameSize(run, size) == null)
  if (dropped.length === 0) return

  for (const size of dropped) store.deselectSize(size)
  if (dropped.length < selected.length) return

  // Nothing survived: fall back to the new-product default the new style
  // offers. With no default size offered either, the (now empty) selection
  // is left for the distributor to fill on Sizes & Colors.
  const defaults = sizesOffered(NEW_PRODUCT_DEFAULT_SIZES, run)
  if (defaults.length > 0) store.updateField('selected_sizes', defaults)
}

function overwriteCopy(store, newMeta) {
  const cleanedTitle = newMeta.name
    ? decodeHtmlEntities(cleanCatalogName(newMeta.name, newMeta.sku))
    : ''
  if (cleanedTitle) {
    store.updateField('product_name', cleanedTitle)
    store.updateField('short_description', cleanedTitle)
  }
  if (newMeta.description) {
    store.updateField('long_description', decodeHtmlEntities(newMeta.description))
  }
}

/**
 * Run the owed clean-up against a resolved catalog payload. Returns true when
 * the payload was the awaited one (handled), false for any other payload, so a
 * late resolution for a superseded pick can never drive it.
 */
export function runSwapCleanup(store, newMeta, { isRenderable, styleUnrenderable = false } = {}) {
  const pending = store.pendingSwapCleanup
  if (!pending || !newMeta?.sku || newMeta.sku !== pending.sku) return false
  store.setPendingSwapCleanup(null)
  // The swap landed: the pre-switch product is no longer a rollback target.
  store.setSupplierSwitchSnapshot?.(null)

  clearListingImages(store)
  reconcileColors(store, pending.oldNames ?? [], newMeta.colors, isRenderable, styleUnrenderable)
  reconcileSizes(store, newMeta)
  overwriteCopy(store, newMeta)
  return true
}

/**
 * The lookup for the awaited style failed: CANCEL the swap (card #349). The
 * swap staged the new SKU / style / supplier before the catalog answered, and
 * a product pointing at an item whose details never loaded is half-swapped
 * (new SKU and cost, old name, no sizes). Put the previous identity back and
 * say so. Written through updateField, so it is a no-op edit when the swap
 * was never saved and a real revert when the autosave already persisted it.
 * Nothing else was changed before the lookup succeeded, so nothing else is
 * undone: the old colors, images, name and sizes are still the product's.
 */
export function failSwapCleanup(store, notify, failedSku = null) {
  const pending = store.pendingSwapCleanup
  if (!pending) return false
  // An error that belongs to another lookup (a superseded swap's) must not
  // roll back THIS swap. Errors that name no SKU are taken as the current one.
  if (failedSku && failedSku !== pending.sku) return false
  store.setPendingSwapCleanup(null)
  store.setSupplierSwitchSnapshot?.(null)
  if (pending.committed) {
    notify?.(SWAP_COMMITTED_LOOKUP_FAILED_MESSAGE)
    return true
  }
  if (pending.previous) {
    // The identity, plus (after a supplier switch) the colors and images the
    // switch cleared: every key the snapshot carries goes back.
    const values = { ...pending.previous }
    // restoreFields, not updateField: it also reaches the server when the
    // swap's autosave is already on the wire.
    store.restoreFields(values)
    // A first SKU pick that failed leaves a SKU-less DRAFT, not a product that
    // lost its SKU: the leave guard must ask again (#233). The autosave of the
    // pick may already have latched hasEverHadSku from the server value.
    if ('product_item_number' in values
        && !(values.product_item_number ?? '').toString().trim()) {
      store.clearEverHadSku?.()
    }
  }
  // The product is back to what it was. After a supplier switch that is the
  // ORIGINAL item (card #542), so it is no longer "SKU cleared": a retry is an
  // ordinary swap that asks to confirm.
  if (typeof pending.previousSkuClearedForSwitch === 'boolean') {
    store.setSkuClearedForSwitch?.(pending.previousSkuClearedForSwitch)
  }
  notify?.(SWAP_LOOKUP_FAILED_MESSAGE)
  return true
}

/**
 * Wire the clean-up to a catalog fetch. Mount this where the fetch lives for
 * the whole editor session (the shell), never in a tab-scoped panel.
 *
 * `renderability` (optional) is `(sku) => Promise<{ isUnsupported(name),
 * styleUnsupported? }>`:
 * the coverage read the default-color pick must obey (a swap's fallback
 * default never lands on a color the editor cannot render). It is awaited
 * BEFORE the clean-up runs; the clean-up re-checks the pending swap after the
 * await, so a swap superseded meanwhile is still dropped. A throwing read
 * fails open (no predicate).
 *
 * @param {{ store: object, meta: import('vue').Ref, error: import('vue').Ref,
 *           notify?: (message: string) => void,
 *           renderability?: (sku: string) => Promise<{isUnsupported: (name: string) => boolean}> }} opts
 */
export function useSkuSwapCleanup({ store, meta, error, notify, renderability }) {
  watch(
    () => meta.value,
    async (newMeta) => {
      if (!newMeta) return
      const pending = store.pendingSwapCleanup
      let isRenderable
      let styleUnrenderable = false
      if (renderability && pending && newMeta.sku === pending.sku) {
        try {
          const coverage = await renderability(newMeta.sku)
          // A style Artifi can render NO color of has no per-color verdicts
          // (empty variant map), so isUnsupported alone would call every
          // color renderable: the style-level verdict makes them all not.
          styleUnrenderable = !!coverage.styleUnsupported
          isRenderable = (c) => !coverage.styleUnsupported
            && !coverage.isUnsupported(c?.name ?? c?.color_sku)
        } catch (err) {
          console.warn('[useSkuSwapCleanup] coverage read failed, picking without it:', err?.message ?? err)
        }
      }
      runSwapCleanup(store, newMeta, { isRenderable, styleUnrenderable })
    },
  )
  watch(
    () => error.value,
    (err) => {
      if (err) failSwapCleanup(store, notify, err.sku ?? null)
    },
  )
}
